import React, { useState } from 'react';
import PlacesAutocomplete, { geocodeByAddress } from 'react-places-autocomplete';
import ErrorMessage from './ErrorMessage';
import Loading from './Loading';
import './AddressLookup.less';
import { StyledInput } from './fields/StyledElements';

const AddressLookup = ({ value, countryRestriction, onChange }: Props) => {
	const [errorMessage, setErrorMessage] = useState<string>(null);

	const handleChange = (address: string) => {
		setErrorMessage(null);
		onChange(address, []);
	};

	const handleSelect = async (address: string) => {
		try {
			const results = await geocodeByAddress(address);
			const addressTuples = results[0]?.address_components
				// Some components are only tagged as "political", which leaves us without a type to map from,
				// so drop those instead of passing an undefined type on to the caller.
				.filter((x) => x.types.some((y) => y !== 'political'))
				.map((x) => [x.types.find((y) => y !== 'political'), x.long_name]);
			onChange('', addressTuples || []);
		} catch (e) {
			// TODO: log to Sentry.
			setErrorMessage(e.toString());
		}
	};

	const searchOptions = countryRestriction ? { componentRestrictions: { country: [countryRestriction] } } : undefined;

	return (
		<>
			<PlacesAutocomplete value={value} onChange={handleChange} onSelect={handleSelect} searchOptions={searchOptions}>
				{({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
					<>
						<StyledInput
							// eslint-disable-next-line react/jsx-props-no-spreading
							{...getInputProps({
								placeholder: '',
								className: 'k-textbox'
							})}
							id="locationSearchInput"
						/>
						{loading && (
							<div className="loaderContainer">
								<Loading loadingPosition=".loaderContainer" />
							</div>
						)}

						{suggestions.length > 0 && !loading && (
							<div className="autocompleteDropdownContainer">
								{suggestions.map((suggestion) => {
									const className = `suggestionItem${suggestion.active ? ' active' : ''}`;
									return (
										<div
											key={suggestion.description}
											// eslint-disable-next-line react/jsx-props-no-spreading
											{...getSuggestionItemProps(suggestion, {
												className
											})}
										>
											<span>{suggestion.description}</span>
										</div>
									);
								})}
							</div>
						)}
					</>
				)}
			</PlacesAutocomplete>
			<ErrorMessage message={errorMessage} />
		</>
	);
};

export default AddressLookup;

interface Props {
	value: string;
	countryRestriction?: string | undefined;
	onChange: (address: string, addressTuples: string[][]) => void;
}

AddressLookup.defaultProps = {
	countryRestriction: null
};
